import { NaceCodeEntity } from '@nk/data-entities-lib';
import { OPERATORS } from '@nk/core/services/company-browser-entities';
import { CompanyBrowserService } from '@nk/core/services/company-browser.service';
import { CompanyBrowserController } from '@nk/core/services/company-browser-controller';
import { arrayToDict, Dict } from '@nk/utils-lib';

export const LAST_LEVEL_NACE_CODE = 5;

export interface NestedSelectableItem {
  id: string;
  label: string;
  tooltipDescription: string;
  level: number;
  isSelected: boolean;
  parentId: string;
  indeterminate: boolean;
  iconClass: string;
}

export function naceCodeLevelFilter(n: NaceCodeEntity): boolean {
  const validLevels = [1, 2, 4, LAST_LEVEL_NACE_CODE];
  return validLevels.includes(n.hierarchy_level);
}

export function sortNaceCodeByHierarchyLevel(items: NaceCodeEntity[]): NaceCodeEntity[] {
  if (!items) {
    return [];
  }
  return items.sort((a: NaceCodeEntity, b: NaceCodeEntity) => {
    if (a.hierarchy_level === b.hierarchy_level) {
      if (a.nace_code_schema === b.nace_code_schema) {
        return a.short_name.localeCompare(b.short_name);
      } else {
        return a.nace_code_schema.localeCompare(b.nace_code_schema);
      }
    } else {
      return a.hierarchy_level - b.hierarchy_level;
    }
  });
}

function createTooltipDescription(nace: NaceCodeEntity): string {
  let tooltipDescription: string;
  if (nace.description.length > 900) {
    tooltipDescription = nace.description.substring(0, 895) + '[...]';
  } else {
    tooltipDescription = nace.description;
  }
  if (!nace.description) {
    tooltipDescription = nace.full_name;
  }
  return tooltipDescription;
}

const HIERARCHY_LEVELS: { [naceCodeHierarchyLevel: number]: number } = {
  1: 0,
  2: 1,
  4: 2,
  [LAST_LEVEL_NACE_CODE]: 3,
};

function findNestedLevelFromNaceHierarchyLevel(nace: NaceCodeEntity): number {
  return HIERARCHY_LEVELS[nace.hierarchy_level];
}

// we will skip some levels, going 1-2-5, and later 1-2-4. (international)
function findConnectedParentUuid(nace: NaceCodeEntity, naceCodeDict: Dict<NaceCodeEntity>): string {
  let parentUuid: string;
  const hierarchyLevelMapping: {
    [naceCodeHierarchyLevel: number]: { topParent: number | null; child: number | null };
  } = {
    1: { topParent: null, child: 4 },
    2: { topParent: 1, child: 4 },
    4: { topParent: 2, child: 5 },
    5: { topParent: 4, child: null },
  };
  if (!hierarchyLevelMapping[nace.hierarchy_level]) {
    console.warn('nace code level not allowed', nace);
  }
  if (nace.hierarchy_level !== 1) {
    const parentHierarchyLevel = hierarchyLevelMapping[nace.hierarchy_level].topParent;

    let iterations = 0;
    let parent = nace;
    let parentLevel = nace.hierarchy_level;
    while (parentLevel !== parentHierarchyLevel) {
      if (parent.parent_nace_code_uuid) {
        parent = naceCodeDict[parent.parent_nace_code_uuid];
      } else {
        console.warn('Could not find parent for nace code', nace);
        break;
      }
      if (iterations > 5) {
        console.error('hit limit');
        break;
      }
      if (parent) {
        parentLevel = parent.hierarchy_level;
        parentUuid = parent.uuid;
      } else {
        console.warn('Could not find parent for nace code', nace);
        break;
      }
      iterations++;
    }
    if (!parentUuid) {
      console.warn('could not find parent for nace code', nace);
    }
  }
  return parentUuid;
}

// when posting nace codes, we deliver the highest value, i.e if a category is selected, we dont give the sub category.
export function updateNaceCodeFromBrowserValues(
  companyBrowser: CompanyBrowserController,
  companyBrowserService: CompanyBrowserService,
  filterFieldKey: string,
  nestedNaceCodes: NestedSelectableItem[],
  naceCodeDict: Dict<NaceCodeEntity>,
): NestedSelectableItem[] {
  const initialNaceCodeMask = companyBrowser.findFilter(filterFieldKey, OPERATORS.Equals);
  const initialSelectedNaceCodes = initialNaceCodeMask
    ? companyBrowserService.base62MaskToNaceCodes(initialNaceCodeMask.value)
    : [];
  const initialSelectedNaceCodesDict = arrayToDict(initialSelectedNaceCodes, (a) => a.uuid);
  return nestedNaceCodes.map((nace) => {
    const naceCode = naceCodeDict[nace.id];
    const isSelected = Boolean(initialSelectedNaceCodesDict[naceCode.uuid]);
    return {
      ...nace,
      isSelected,
    };
  });
}

export function getNestedSelectableItem(
  naceCode: NaceCodeEntity,
  naceCodeDict: Dict<NaceCodeEntity>,
): NestedSelectableItem {
  return {
    id: naceCode.uuid,
    label: naceCode.short_name + ' (' + naceCode.nace_code + ')',
    tooltipDescription: createTooltipDescription(naceCode),
    level: findNestedLevelFromNaceHierarchyLevel(naceCode),
    parentId: findConnectedParentUuid(naceCode, naceCodeDict),
    isSelected: false,
    indeterminate: false,
    iconClass: getFlagIcon(naceCode.nace_code_schema),
  };
}

enum AllowedCountryCode {
  NO = 'no',
  DK = 'dk',
  FI = 'fi',
  SE = 'se',
}

export function getFlagIcon(naceCodeSchema: string): string {
  return AllowedCountryCode[naceCodeSchema] ? `flag flag-${naceCodeSchema.toLowerCase()}` : '';
}

export function getFirstAndLastNaceCodeHierarchyLevel(naceCodes: NaceCodeEntity[]): {
  firstLevel: number;
  lastLevel: number;
} {
  const naceCodesSortedByHierarchyLevel = naceCodes.sort((a, b) => (a.hierarchy_level < b.hierarchy_level ? -1 : 1));

  return {
    firstLevel: naceCodesSortedByHierarchyLevel[0].hierarchy_level,
    lastLevel: naceCodesSortedByHierarchyLevel[naceCodesSortedByHierarchyLevel.length - 1].hierarchy_level,
  };
}
